import { valibotResolver } from "@hookform/resolvers/valibot";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";

import useApiTokensMutations from "../../../datahooks/useApiTokensMutations";
import { ApiKeyNameSchema } from "../../../schema";
import { formatBillingDate } from "../../../utils";
import ApiTokenTag from "../ApiTokenTag";

import type { ApiToken } from "../../../types";

interface TokenDetailsProps {
  selectedToken: ApiToken;
  onDelete: () => void;
  onClose: () => void;
}

function Separator() {
  return <div className="h-px w-full bg-black-200" />;
}

export default function TokenDetails({
  selectedToken,
  onDelete,
  onClose,
}: TokenDetailsProps) {
  const {
    name,
    id,
    token: tokenId,
    created_at: createdAt,
    write: canWrite,
    read: canRead,
  } = selectedToken;

  const { updateApiToken, isUpdatingToken } = useApiTokensMutations();

  const {
    register,
    reset: resetForm,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    name: string;
  }>({
    defaultValues: { name },
    resolver: valibotResolver(ApiKeyNameSchema),
  });

  useEffect(() => {
    resetForm({ name });
  }, [selectedToken, resetForm]);

  const permissions = canWrite && canRead ? "All" : "Read only";

  function onSubmit(data: { name: string }): void {
    updateApiToken({
      tokenId: id,
      updates: { name: data.name },
    }).then(() => {
      onClose();
    });
  }

  return (
    <>
      <h2 className="mb-8 line-clamp-1 break-words text-2xl-bold">{name}</h2>

      <form className="flex flex-col gap-y-4" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          label="Key name"
          htmlFor="key-name"
          error={errors.name?.message}
        >
          <Input {...register("name")} placeholder="Key name" id="key-name" />
        </FormField>

        <Separator />

        <div className="grid grid-cols-2">
          <span className="text-black-400">Permissions</span>

          <span className="text-sm-bold capitalize">{permissions}</span>
        </div>

        <Separator />

        <div className="grid grid-cols-2">
          <span className="text-black-400">Key</span>

          <ApiTokenTag tokenId={tokenId} />
        </div>

        <Separator />

        <div className="grid grid-cols-2">
          <span className="text-black-400">Created at</span>

          <span className="text-sm-bold">{formatBillingDate(createdAt)}</span>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <Button isLoading={isUpdatingToken} size="lg" type="submit">
            Save
          </Button>

          <Button
            variant="outline-danger"
            size="lg"
            disabled={isUpdatingToken}
            onClick={onDelete}
          >
            Delete
          </Button>
        </div>
      </form>
    </>
  );
}
